import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getNews } from '../services/firestoreService';
import { NewsItem as NewsItemType } from '../types';
import { OptimizedImage } from './OptimizedImage';
import { articleExcerpt } from '../utils/article';
import { Card, SectionHeader } from './ui';

const News = () => {
  const [news, setNews] = useState<NewsItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsData = await getNews();
        setNews(newsData.slice(0, 3)); // The full archive lives on /actualites
        setLoading(false);
      } catch (err) {
        console.error("Error fetching news:", err);
        setError("Erreur lors du chargement des actualités");
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Loading, error and empty states
  if (loading || error || !news.length) {
    const message = loading
      ? "Chargement des actualités..."
      : error
        ? error
        : "Aucune actualité disponible pour le moment.";

    const textColor = error ? "text-red-600" : "text-brand-pink-600";

    return (
      <div className="relative py-24">
        <div className="relative text-center py-12">
          <p className={`${textColor} text-lg`}>{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative py-20 md:py-24 overflow-hidden">
      <div className="relative container mx-auto px-4">
        <SectionHeader
          kicker="Le journal"
          title="Dernières actualités"
          lead="Restez informé des dernières actions et initiatives qui font battre le cœur de Nous'Rire."
        />

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
        >
          {news.map(item => (
            <NewsCard key={item.id} item={item} />
          ))}
        </motion.div>

        <div className="mt-10 text-center">
          <Link to="/actualites" className="btn-secondary">
            Toutes les actualités <ArrowLongRightIcon className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export const NewsCard = ({ item }: { item: NewsItemType }) => {
  return (
    <motion.article
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.215, 0.61, 0.355, 1] } }
      }}
      className="h-full"
    >
      <Link to={`/actualites/${item.id}`} className="group block h-full rounded-2xl">
        <Card className="h-full overflow-hidden flex flex-col">
          {item.image && (
            <div className="overflow-hidden">
              <LazyNewsImage src={item.image} alt={item.title} />
            </div>
          )}
          <div className="p-7 flex flex-col flex-1">
            <span className="eyebrow mb-3">
              {format(new Date(item.date), 'd MMMM yyyy', { locale: fr })}
            </span>
            <h3 className="heading text-xl mb-3 line-clamp-2">
              {item.title}
            </h3>
            <p className="text-brand-pink-900/75 leading-relaxed line-clamp-4">
              {articleExcerpt(item.content)}
            </p>
            <span className="mt-auto pt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent group-hover:underline underline-offset-4">
              Lire la suite <ArrowLongRightIcon className="h-4 w-4" />
            </span>
          </div>
        </Card>
      </Link>
    </motion.article>
  );
};

// Lazy loading image component
export function LazyNewsImage({ src, alt }: { src: string; alt: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    // Store the current ref value
    const currentRef = imgRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      // No need to check imgRef.current here
      // The disconnect() method clears all observations
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={imgRef} className="relative h-52 w-full">
      {isVisible && !hasError ? (
        <OptimizedImage
          src={src}
          imageName={src.split('/').pop() || ''}
          alt={alt}
          width={400}
          height={300}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      ) : isVisible && hasError ? (
        <OptimizedImage
          src="/images/news-placeholder.webp"
          imageName="news-placeholder.webp"
          alt={alt}
          width={800}
          height={600}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-brand-pink-100 animate-pulse"></div>
      )}
    </div>
  );
}

export default News;